import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Content from "../..//layout/content/Content";
import Head from "../../layout/head/Head";
import {
  Card,
  Row,
  Col,
  Button,
  Spinner,
  Alert,
  Badge,
  Dropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
} from "reactstrap";
import {
  Block,
  BlockBetween,
  BlockHead,
  BlockHeadContent,
  BlockTitle,
  Icon,
} from "../../components/Component";
import {
  Table,
  Collapse as AntCollapse,
  TableColumnsType,
  Timeline,
  Popover,
  Empty,
  Flex,
  Spin,
  Tag,
  Modal,
  Descriptions,
} from "antd";
import {
  PaperClipOutlined,
  DownloadOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  DownOutlined,
  RightOutlined,
  EditOutlined,
  CopyOutlined,
  DeleteOutlined,
  FileTextOutlined,
  DollarOutlined,
} from "@ant-design/icons";
import {
  PendingWorkOrderData,
  WorkOrderData,
  WorkOrderLine,
  WorkOrderLineSource,
  PaymentCondition,
} from "./types";
import {
  getWorkOrderAmendmentById,
  WorkOrderAmendmentData,
  ChangeRequestStatus,
  ChangeRequestComment,
  ChangeRequestFile,
  getChangeRequestStatusColor,
  getFileIcon,
  formatFileSize,
  getPendingWorkOrderById,
} from "./mockData/workOrderAmendmentData";
import workOrderApis from "../../api/WorkOrderManagement/WorkOrderApis";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import Swal from "sweetalert2";
import { formatDecimal } from "../../utils/Utils";
import { message } from "antd";

dayjs.extend(relativeTime);

const { Panel } = AntCollapse;

// Helper function to check if payment conditions is in array format
const isPaymentConditionsArray = (
  paymentConditions: any
): paymentConditions is PaymentCondition[] => {
  return Array.isArray(paymentConditions);
};

const WorkOrderView: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [workOrder, setWorkOrder] = useState<WorkOrderData | null>(null);
  const [amendmentData, setAmendmentData] = useState<any | null>(null);
  const [pendingApprovalsData, setPendingApprovals] =
    useState<PendingWorkOrderData | null>(null);
  const [isAmendmentView, setIsAmendmentView] = useState(false);
  const [isPendingApprovalsWorkOrderView, setIsPendingApprovalsWorkOrderView] =
    useState(false);
  const [loading, setLoading] = useState(true);
  const [screenSize, setScreenSize] = useState(0);
  const [mobileView, setMobileView] = useState(false);
  const [sm, setSm] = useState(false);
  const [disableApprove, setDisableApprove] = useState(false);
  const [disableReject, setDisableReject] = useState(false);
  const [shareOptionsToggle, setShareOptionsToggle] = useState(false);

  // Mock current user permissions
  const [allowedActions] = useState({
    view: 1,
    edit: 1,
    approve: 1,
    reject: 1,
  });

  const [currentUserId] = useState(1); // Mock current user ID
  const [approverIds] = useState([1, 2, 3]); // Mock approver IDs

  useEffect(() => {
    if (id) {
      loadWorkOrderData(id);
    }
    viewChange();
    window.addEventListener("load", viewChange);
    window.addEventListener("resize", viewChange);
    const headerClick = () => setSm(false);
    document
      .getElementsByClassName("nk-header")[0]
      ?.addEventListener("click", headerClick);

    return () => {
      window.removeEventListener("resize", viewChange);
      window.removeEventListener("load", viewChange);
      document
        .getElementsByClassName("nk-header")[0]
        ?.removeEventListener("click", headerClick);
    };
  }, [id]);

  const viewChange = () => {
    setScreenSize(window.innerWidth);
    if (window.innerWidth < 990) {
      setMobileView(true);
    } else {
      setMobileView(false);
      setSm(false);
    }
  };

  const loadWorkOrderData = async (workOrderId: string) => {
    try {
      setLoading(true);

      if (!workOrderId) {
        message.error("Work Order ID is required");
        setLoading(false);
        return;
      }

      console.log("Fetching Work Order with ID:", workOrderId);

      const response: any = await workOrderApis.getWorkOrderById(workOrderId);

      if (response && response.status && response.data) {
        const apiData = response.data;
        console.log("API Response:", apiData);

        // Map work order lines with proper structure
        const mappedWorkOrderLines =
          apiData.workOrderLines?.map((line: any) => ({
            id: line.workOrderLineId?.toString() || "",
            taskTitle: line.taskTitle || "",
            description: line.description || "",
            workCategory: line.workCategoryId?.toString() || "",
            workCategoryName: line.workCategoryName || "",
            quantity: line.quantity || 0,
            uom: line.uomId?.toString() || "",
            uomName: line.uomName || "",
            allocatedQty: line.allocatedQty || 0,
            weightagePct: line.weightagePct || 0,
            rate: line.rate || 0,
            taxType: line.taxId?.toString() || "",
            taxTypeName: line.taxName || "",
            taxPercentage: line.taxRate || 0,
            taxAmount: line.taxAmount || 0,
            sgstRate: line.sgstRate || 0,
            cgstRate: line.cgstRate || 0,
            igstRate: line.igstRate || 0,
            lineValue: line.lineValue || 0,
            sequence: line.sequence || 1,
            source: line.sourceType || "New Work Line",
            status: line.status || "Active",
            uploadedDocs: line.dms || [],
            // Budget allocation fields
            budgetLineId: line.budgetLineId || 0,
            budgetLineName: line.budgetLineName || "",
            budgetAllocation: line.budgetAllocation || 0,
          })) || [];

        // Map general conditions
        const mappedGeneralConditions =
          apiData.workOrderGeneralConditions?.map((condition: any) => ({
            id: condition.workOrderConditionId?.toString() || "",
            conditionCode: condition.conditionCode || "",
            content: condition.content || "",
            createdAt: condition.createdAt || "",
            updatedAt: condition.updatedAt || "",
          })) || [];

        // Map payment conditions
        const mappedPaymentConditions =
          apiData.workOrderPaymentConditions?.map((condition: any) => ({
            id: condition.workOrderPaymentTermId?.toString() || "",
            conditionType: condition.conditionType || "",
            isPercent: condition.isPercent || false,
            percentValue: condition.percentValue || 0,
            amountValue: condition.amountValue || 0,
            interestCharged: condition?.interestCharged || false,
            interestRate: condition.interestRate || 0,
            recoveryType: condition.recoveryType || "",
            releaseDate: condition.releaseDate || "",
            notes: condition.notes || "",
            calculatedAmount: condition.calculatedAmount || 0,
            createdAt: condition.createdAt || "",
            updatedAt: condition.updatedAt || "",
          })) || [];

        // Map approvals
        const mappedApprovals =
          apiData.workOrderApprovals?.map((approval: any) => ({
            id: approval.workOrderApprovalId?.toString() || "",
            userId: approval.userId || "",
            approverName: approval.approverName || "",
            approverRole: approval.approverRole || "",
            level: approval.level || "",
            status: approval.status || "",
            approvalStage: approval.approvalStage || "",
            createdAt: approval.createdAt || "",
          })) || [];

        // Map API response to WorkOrderData interface
        const mappedWorkOrder: any = {
          id: apiData.workOrderId?.toString() || workOrderId,
          workOrderNumber: apiData.workOrderNumber || "",
          workOrderTitle: apiData.workOrderTitle || "",
          description: apiData.description || "",
          contractDate: apiData.contractDate || "",
          siteId: apiData.siteId?.toString() || "",
          siteName: apiData.siteName || "",
          projectName: apiData.projectName || "",
          contractType: apiData.contractType || "",
          contractWith: apiData.contractWith || "",
          contractorId: apiData.contractorId?.toString() || "",
          contractorName: apiData.contractorName || "",
          workOrderOwnerId: apiData.workOrderOwnerId?.toString() || "",
          workOrderOwnerName: apiData.workOrderOwnerName || "",
          plannedStartDate: apiData.plannedStartDate || "",
          plannedEndDate: apiData.plannedEndDate || "",
          approvalRequired: apiData.approvalRequired || false,
          enableTax: apiData.enableTax || false,
          taxApplicationLevel: apiData.taxLevel || "",
          headerTaxId: apiData.headerTaxId,
          headerTaxName: apiData.headerTaxName || "",
          tdsTaxId: apiData.tdsTaxId,
          tdsTaxName: apiData.tdsTaxName || "",
          tdsRate: apiData.tdsRate || 0,
          tcsTaxId: apiData.tcsTaxId,
          tcsTaxName: apiData.tcsTaxName || "",
          tcsRate: apiData.tcsRate || 0,
          budgetId: apiData.budgetId,
          budgetName: apiData.budgetName || "",
          status: apiData.status || "Draft",
          totalValue: apiData.totalValue || 0,
          taxAmount: apiData.taxAmount || 0,
          grossValue: apiData.grossValue || 0,
          outstandingAmount: apiData.outstandingAmount || 0,
          createdBy: apiData.createdByName || "Unknown",
          createdAt: apiData.createdAt || new Date().toISOString(),
          updatedAt: apiData.updatedAt || apiData.createdAt,
          updatedBy: apiData.updatedByName || "",
          orgName: apiData.orgName || "",
          orgId: apiData.orgId?.toString() || "",
          // Budget information
          budgetHeaderId: apiData.budgetHeaderId || 0,
          // budgetName: apiData.budgetName || "",
          // Mapped arrays
          workOrderLines: mappedWorkOrderLines,
          generalConditions: mappedGeneralConditions,
          paymentConditions: mappedPaymentConditions,
          approvals: mappedApprovals,
          attachments: apiData.attachments || [],
          dms: apiData.dms || [],
          contractorChangeRequests: apiData.contractorChangeRequests || [],
        };

        // Check if this work order is in amendment mode (Changes Requested status)
        const isAmendment = mappedWorkOrder.status === "Changes Requested";

        //  const isAmendment =
        // mappedWorkOrder.status === "Changes Requested" ||
        // (mappedWorkOrder.contractorChangeRequests &&
        //   mappedWorkOrder.contractorChangeRequests.length > 0);

        // Map contractor change requests to amendment data format
        const mappedAmendmentData =
          mappedWorkOrder.contractorChangeRequests &&
          mappedWorkOrder.contractorChangeRequests.length > 0
            ? {
                changeRequestStatus:
                  mappedWorkOrder.status === "Changes Requested"
                    ? "Changes Requested"
                    : "Change Requested",
                changeRequestedBy:
                  mappedWorkOrder.contractorChangeRequests[0]?.createdByName ||
                  "Unknown",
                changeRequestDate:
                  mappedWorkOrder.contractorChangeRequests[0]?.createdAt ||
                  new Date().toISOString(),
                changeRequestComments:
                  mappedWorkOrder.contractorChangeRequests.map(
                    (request: any) => ({
                      commentBy: request.createdByName || "Unknown",
                      commentRole: "Contractor",
                      commentDate:
                        request.createdAt || new Date().toISOString(),
                      commentText: request.changeReason || "No reason provided",
                      changeType: "Amendment Request",
                    })
                  ),
                changeRequestFiles:
                  mappedWorkOrder.contractorChangeRequests.reduce(
                    (files: any[], request: any) => {
                      if (request.dms && Array.isArray(request.dms)) {
                        const requestFiles = request.dms.map((file: any) => ({
                          fileName: file.fileName || "Unknown File",
                          fileType: file.fileType || "application/octet-stream",
                          fileSize: file.fileSize || 0,
                          category: file.moduleFileType || "General",
                          uploadedBy: request.createdByName || "Unknown",
                          uploadedDate:
                            request.createdAt || new Date().toISOString(),
                          description: `Change request attachment`,
                          filePath: file.filePath || "",
                          docId: file.docId,
                        }));
                        return [...files, ...requestFiles];
                      }
                      return files;
                    },
                    []
                  ),
              }
            : null;

        setWorkOrder(mappedWorkOrder);
        setIsAmendmentView(isAmendment);
        setAmendmentData(mappedAmendmentData);
        setIsPendingApprovalsWorkOrderView(false);
        message.success("Work Order details loaded successfully");
      } else {
        throw new Error(
          response?.message || "Failed to fetch Work Order details"
        );
      }
      // }
    } catch (error: any) {
      console.error("Error loading work order:", error);
      message.error("Failed to load work order data");
      Swal.fire({
        icon: "error",
        title: "Error",
        text:
          error.message || "Failed to load work order data. Please try again.",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = () => {
    if (id) {
      navigate(`/work-order-creation/?edit=${id}`);
    }
  };

  const handleDuplicate = () => {
    if (id) {
      navigate(`/work-order-creation/?duplicate=${id}`);
    }
  };

  const handleDelete = () => {
    if (!workOrder) return;

    Swal.fire({
      title: "Are you sure?",
      text: "This action cannot be undone!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete it!",
    }).then((result) => {
      if (result.isConfirmed) {
        // Mock delete functionality
        Swal.fire("Deleted!", "Work Order has been deleted.", "success").then(
          () => {
            navigate("/work-orders/view-work-orders");
          }
        );
      }
    });
  };

  const handleApprove = async () => {
    try {
      setDisableApprove(true);
      // Simulate API call
      await new Promise((resolve) => setTimeout(resolve, 1000));

      // Mock approve functionality
      const updatedWorkOrder: any = { ...workOrder!, status: "Approved" };
      setWorkOrder(updatedWorkOrder);

      Swal.fire(
        "Approved!",
        "Work Order has been approved successfully",
        "success"
      ).then(() => {
        navigate("/work-orders/view-work-orders");
      });
    } catch (error) {
      console.error(error);
      Swal.fire("Error!", "Failed to approve Work Order", "error");
    } finally {
      setDisableApprove(false);
    }
  };

  const handleReject = async (rejectedReason: string) => {
    try {
      setDisableReject(true);
      // Simulate API call
      await new Promise((resolve) => setTimeout(resolve, 1000));

      // Mock reject functionality
      const updatedWorkOrder: any = {
        ...workOrder!,
        status: "Rejected",
        rejectionComments: rejectedReason,
      };
      setWorkOrder(updatedWorkOrder);

      Swal.fire("Rejected!", "Work Order has been rejected", "success").then(
        () => {
          navigate("/work-orders/view-work-orders");
        }
      );
    } catch (error) {
      console.error(error);
      Swal.fire("Error!", "Failed to reject Work Order", "error");
    } finally {
      setDisableReject(false);
    }
  };

  const showRejectModal = () => {
    Swal.fire({
      title: "Reject Work Order",
      html: `
        <textarea 
          id="rejection-reason" 
          placeholder="Enter rejection reason (required)"
          style="width: 100%; height: 100px; margin-top: 10px; padding: 8px; border: 1px solid #ddd; border-radius: 4px;"
        ></textarea>
      `,
      showCancelButton: true,
      confirmButtonColor: "#dc3545",
      cancelButtonColor: "#6c757d",
      confirmButtonText: "Reject",
      cancelButtonText: "Cancel",
      preConfirm: () => {
        const reason = (
          document.getElementById("rejection-reason") as HTMLTextAreaElement
        )?.value;
        if (!reason.trim()) {
          Swal.showValidationMessage("Rejection reason is required");
          return false;
        }
        return reason;
      },
    }).then((result: any) => {
      if (result.isConfirmed) {
        handleReject(result.value);
      }
    });
  };

  // Amendment specific handlers
  const handleApproveAmendment = async () => {
    try {
      setDisableApprove(true);
      await new Promise((resolve) => setTimeout(resolve, 1000));

      Swal.fire(
        "Approved!",
        "Work Order amendment has been approved successfully",
        "success"
      ).then(() => {
        navigate("/work-order-contractor-approvals");
      });
    } catch (error) {
      console.error(error);
      Swal.fire("Error!", "Failed to approve amendment", "error");
    } finally {
      setDisableApprove(false);
    }
  };

  const handleRejectAmendment = async (rejectedReason: string) => {
    try {
      setDisableReject(true);
      await new Promise((resolve) => setTimeout(resolve, 1000));

      Swal.fire(
        "Rejected!",
        "Work Order amendment has been rejected",
        "success"
      ).then(() => {
        navigate("/work-order-contractor-approvals");
      });
    } catch (error) {
      console.error(error);
      Swal.fire("Error!", "Failed to reject amendment", "error");
    } finally {
      setDisableReject(false);
    }
  };

  const handleReleaseWorkOrder = async () => {
    try {
      if (!workOrder?.id) {
        message.error("Work Order ID is required");
        return;
      }

      // Show confirmation dialog
      const result = await Swal.fire({
        title: "Release Work Order?",
        text: "Are you sure you want to release this work order? This action cannot be undone.",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#28a745",
        cancelButtonColor: "#6c757d",
        confirmButtonText: "Yes, Release Work Order",
        cancelButtonText: "Cancel",
      });

      if (result.isConfirmed) {
        // Prepare request body
        const requestBody = {
          workOrderId: workOrder.id,
        };

        console.log("Releasing work order:", requestBody);

        // Call the release API
        const response: any = await workOrderApis.releaseWorkOrder(requestBody);

        if (response && response.status) {
          Swal.fire(
            "Released!",
            "Work Order has been released successfully",
            "success"
          ).then(() => {
            // Optionally navigate back to work orders list
            navigate("/view-work-orders");
          });
        } else {
          throw new Error(response?.message || "Failed to release work order");
        }
      }
    } catch (error: any) {
      console.error("Error releasing work order:", error);
      Swal.fire(
        "Error!",
        error.message || "Failed to release work order. Please try again.",
        "error"
      );
    }
  };

  const showRejectAmendmentModal = () => {
    Swal.fire({
      title: "Reject Amendment Request",
      html: `
        <textarea 
          id="rejection-reason" 
          placeholder="Enter rejection reason (required)"
          style="width: 100%; height: 100px; margin-top: 10px; padding: 8px; border: 1px solid #ddd; border-radius: 4px;"
        ></textarea>
      `,
      showCancelButton: true,
      confirmButtonColor: "#dc3545",
      cancelButtonColor: "#6c757d",
      confirmButtonText: "Reject Amendment",
      cancelButtonText: "Cancel",
      preConfirm: () => {
        const reason = (
          document.getElementById("rejection-reason") as HTMLTextAreaElement
        )?.value;
        if (!reason.trim()) {
          Swal.showValidationMessage("Rejection reason is required");
          return false;
        }
        return reason;
      },
    }).then((result: any) => {
      if (result.isConfirmed) {
        handleRejectAmendment(result.value);
      }
    });
  };

  const downloadFile = (file: any) => {
    if (file.filePath) {
      // Open the file URL in a new tab to download
      window.open(file.filePath, "_blank");
    } else {
      Swal.fire("Info", `Downloading ${file.fileName}...`, "info");
    }
  };

  const viewFile = (file: any) => {
    if (file.filePath) {
      // Open the file URL in a new tab to view
      window.open(file.filePath, "_blank");
    } else {
      Swal.fire("Info", `Opening ${file.fileName}...`, "info");
    }
  };

  // Helper function to get file icon based on file type
  const getFileIcon = (fileType: string) => {
    if (fileType.includes("image")) return "🖼️";
    if (fileType.includes("pdf")) return "📄";
    if (fileType.includes("word")) return "📝";
    if (fileType.includes("excel") || fileType.includes("sheet")) return "📊";
    return "📎";
  };

  // Helper function to format file size
  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  const getStatusColor = (status: string): string => {
    const statusColors: { [key: string]: string } = {
      Draft: "secondary",
      "Pending Approval": "warning",
      Approved: "success",
      Rejected: "danger",
      "Changes Requested": "warning",
      Released: "success",
      "Contractor Confirmed": "primary",
      "In Execution": "info",
      "On Hold": "warning",
      Completed: "success",
      Closed: "secondary",
      Cancelled: "danger",
    };
    return statusColors[status] || "secondary";
  };

  const getSourceColor = (source: WorkOrderLineSource) => {
    const colors = {
      [WorkOrderLineSource.ProjectManagement]: "blue",
      [WorkOrderLineSource.WorkBOQ]: "green",
      [WorkOrderLineSource.NewWorkLine]: "orange",
    };
    return colors[source] || "default";
  };

  // Work Order Lines Table Columns
  const workOrderLinesColumns: TableColumnsType<any> = [
    // {
    //   title: "#",
    //   key: "#",
    //   width: 50,
    //   fixed: "left",
    //   render: (text, record, index) => index + 1,
    // },
    // {
    //   title: "Line #",
    //   dataIndex: "lineNumber",
    //   key: "lineNumber",
    //   width: 80,
    //   fixed: "left",
    // },
    {
      title: "Task Title",
      dataIndex: "taskTitle",
      key: "taskTitle",
      width: 200,
      render: (text: string, record: WorkOrderLine) => (
        <div>
          <div className="fw-bold">{text}</div>
          {record.taskDescription && (
            <small className="text-muted">{record.taskDescription}</small>
          )}
        </div>
      ),
    },
    {
      title: "Category",
      dataIndex: "workCategoryName",
      key: "workCategoryName",
      width: 120,
      render: (text: string, record: WorkOrderLine) => (
        <div>
          <div>{text}</div>
          {record.subCategory && (
            <small className="text-muted">{record.subCategory}</small>
          )}
        </div>
      ),
    },
    {
      title: "Quantity",
      dataIndex: "quantity",
      key: "quantity",
      width: 100,
      render: (quantity: number, record: any) =>
        `${formatDecimal(quantity)} ${record?.uomName}`,
    },
    {
      title: "UOM",
      dataIndex: "uomName",
      key: "uomName",
      width: 80,
    },
    {
      title: "Rate",
      dataIndex: "rate",
      key: "rate",
      width: 120,
      render: (rate: number) => `₹${rate?.toLocaleString()}`,
    },
    {
      title: "Tax",
      key: "tax",
      width: 120,
      render: (_: any, record: any) => (
        <div>
          {record.taxTypeName && (
            <div className="small">{record.taxTypeName}</div>
          )}
          {(record.sgstRate > 0 ||
            record.cgstRate > 0 ||
            record.igstRate > 0) && (
            <div className="small text-muted">
              {record.sgstRate > 0 && record.cgstRate > 0 ? (
                <>
                  SGST: {record.sgstRate}% + CGST: {record.cgstRate}%
                </>
              ) : record.igstRate > 0 ? (
                <>IGST: {record.igstRate}%</>
              ) : (
                <>{record.taxPercentage}%</>
              )}
            </div>
          )}
          <div className="fw-bold">
            ₹{record.taxAmount?.toLocaleString() || 0}
          </div>
        </div>
      ),
    },
    {
      title: "Total Amount",
      key: "lineValue",
      width: 140,
      render: (_: any, record: any) => `₹${record.lineValue?.toLocaleString()}`,
    },
    {
      title: "Source",
      dataIndex: "source",
      key: "source",
      width: 140,
      render: (source: string) => (
        <Tag color={getSourceColor(source as WorkOrderLineSource)}>
          {source}
        </Tag>
      ),
    },
    {
      title: "Budget Details",
      key: "budgetDetails",
      width: 200,
      render: (_: any, record: any) => (
        <div>
          {record.budgetLineName ? (
            <div>
              <div className="fw-bold small text-primary">
                {record.budgetLineName}
              </div>
              <div className="small text-success">
                Allocated: ₹{record.budgetAllocation?.toLocaleString() || 0}
              </div>
              {/* {record.budgetLineId && (
                <div className="small text-muted">
                  Budget Line ID: {record.budgetLineId}
                </div>
              )} */}
            </div>
          ) : (
            <div className="small text-muted">No budget allocation</div>
          )}
        </div>
      ),
    },
  ];

  const getStatusMapper = (status: string) => {
    const statusMap: { [key: string]: { title: string; color: string } } = {
      Draft: { title: "Draft", color: "secondary" },
      "Pending Approval": { title: "Pending Approval", color: "warning" },
      Approved: { title: "Approved", color: "success" },
      Rejected: { title: "Rejected", color: "danger" },
      "Changes Requested": { title: "Changes Requested", color: "warning" },
      Released: { title: "Released", color: "success" },
      "Contractor Confirmed": {
        title: "Contractor Confirmed",
        color: "primary",
      },
      "In Execution": { title: "In Execution", color: "info" },
      "On Hold": { title: "On Hold", color: "warning" },
      Completed: { title: "Completed", color: "success" },
      Closed: { title: "Closed", color: "secondary" },
      Cancelled: { title: "Cancelled", color: "danger" },
    };
    return statusMap[status] || { title: status, color: "secondary" };
  };

  if (loading) {
    return (
      <>
        <Head title="Work Order Details" />
        <Content>
          <Flex align="center" gap="middle" className="pageLoader">
            <Spin size="large" className="m-auto" />
          </Flex>
        </Content>
      </>
    );
  }

  if (!workOrder) {
    return (
      <>
        <Head title="Work Order Not Found" />
        <Content>
          <div className="text-center py-5">
            <Empty description="Work Order not found" />
          </div>
        </Content>
      </>
    );
  }

  const statusInfo = getStatusMapper(workOrder.status);

  // Calculate totals
  const subtotal =
    workOrder.workOrderLines?.reduce(
      (sum, line) => sum + line.quantity * line.rate,
      0
    ) || 0;
  const lineLevelTaxAmount =
    workOrder.workOrderLines?.reduce(
      (sum, line) => sum + (line.taxAmount || 0),
      0
    ) || 0;

  // Calculate header level tax amounts (TDS and TCS)
  const tdsAmount =
    workOrder.tdsRate && subtotal > 0
      ? (subtotal * workOrder.tdsRate) / 100
      : 0;
  const tcsAmount =
    workOrder.tcsRate && subtotal > 0
      ? (subtotal * workOrder.tcsRate) / 100
      : 0;

  // Total tax includes line level + TCS (TDS is deducted later)
  const totalTaxAmount = lineLevelTaxAmount + tcsAmount;
  const grossTotal = subtotal + totalTaxAmount;

  // Calculate payment conditions deductions (Retention + On Hold, excluding Mobilization advance)
  const paymentDeductions =
    workOrder?.paymentConditions &&
    isPaymentConditionsArray(workOrder.paymentConditions)
      ? (workOrder.paymentConditions as PaymentCondition[]).reduce(
          (sum: number, condition: PaymentCondition) =>
            condition.conditionType !== "Mobilization"
              ? sum + (condition.calculatedAmount || 0)
              : sum,
          0
        )
      : 0;

  // Net Payable = Gross Total - TDS - Payment Deductions (Retention + On Hold)
  const netPayable = grossTotal - tdsAmount - paymentDeductions;

  return (
    <>
      <Head title={`Work Order - ${workOrder.workOrderNumber}`} />
      <Content>
        <Card className="card-bordered">
          <div className="card-aside-wrap">
            <div className="card-inner card-inner-lg">
              {sm && mobileView && (
                <div
                  className="toggle-overlay"
                  onClick={() => setSm(!sm)}
                ></div>
              )}
              <BlockHead size="lg">
                <BlockBetween>
                  <BlockHeadContent>
                    <BlockTitle tag="h4">
                      <span>
                        {workOrder.workOrderNumber} | {statusInfo.title}
                      </span>
                      {/* {isAmendmentView && amendmentData && (
                        <Badge
                          color={getChangeRequestStatusColor(
                            amendmentData.changeRequestStatus
                          )}
                          className="ml-2"
                        >
                          {amendmentData.changeRequestStatus}
                        </Badge>
                      )} */}
                    </BlockTitle>
                  </BlockHeadContent>
                  <div className="d-flex">
                    {/* Amendment specific actions */}
                    {isAmendmentView &&
                      amendmentData &&
                      amendmentData.changeRequestStatus ===
                        ChangeRequestStatus.ChangeRequested && (
                        <div className="actions clearfix">
                          <ul
                            className="m-auto d-flex align-items-center justify-content-center p-0"
                            style={{ flexDirection: "row-reverse" }}
                          ></ul>
                        </div>
                      )}

                    {/* Edit Work Order button for amendments and drafts */}
                    {(isAmendmentView ||
                      workOrder.status === "Draft" ||
                      workOrder.status === "Changes Requested") && (
                      <div className="actions clearfix">
                        <ul
                          className="m-auto d-flex align-items-center justify-content-center p-0"
                          style={{ flexDirection: "row-reverse" }}
                        >
                          <li className="pt-0 pb-0">
                            <Button color="primary" onClick={handleEdit}>
                              <EditOutlined /> Edit Work Order
                            </Button>
                          </li>
                        </ul>
                      </div>
                    )}

                    {/* Original action buttons for non-amendment cases */}
                    {!isAmendmentView && workOrder.status === "Draft" && (
                      <div className="actions clearfix">
                        <ul
                          className="m-auto d-flex align-items-center justify-content-center p-0"
                          style={{ flexDirection: "row-reverse" }}
                        >
                          <li className="pt-0 pb-0">
                            <Button color="secondary" onClick={handleDuplicate}>
                              <CopyOutlined /> Duplicate
                            </Button>
                          </li>
                          <li className="pt-0 pb-0">
                            <Button color="danger" onClick={handleDelete}>
                              <DeleteOutlined /> Delete
                            </Button>
                          </li>
                        </ul>
                      </div>
                    )}

                    {(!isAmendmentView &&
                      workOrder.status === "Pending Approval" &&
                      allowedActions?.approve === 1 &&
                      approverIds.includes(currentUserId)) ||
                      (isPendingApprovalsWorkOrderView && (
                        <div className="actions clearfix">
                          <ul
                            className="m-auto d-flex align-items-center justify-content-center p-0"
                            style={{ flexDirection: "row-reverse" }}
                          >
                            <li className="pt-0 pb-0">
                              {disableApprove ? (
                                <Button color="success" type="button">
                                  <Spinner size="sm" color="light" />
                                </Button>
                              ) : (
                                <Button
                                  color="success"
                                  type="button"
                                  onClick={() => {
                                    Swal.fire({
                                      title: "Are you sure?",
                                      text: "Do you want to approve this Work Order?",
                                      icon: "warning",
                                      showCancelButton: true,
                                      confirmButtonText: "Yes, Approve it!",
                                      cancelButtonText: "No, keep it",
                                    }).then((result) => {
                                      if (result.isConfirmed) {
                                        handleApprove();
                                      }
                                    });
                                  }}
                                >
                                  Approve
                                </Button>
                              )}
                            </li>
                            <li className="pt-0 pb-0">
                              {disableReject ? (
                                <Button color="danger" type="button">
                                  <Spinner size="sm" color="light" />
                                </Button>
                              ) : (
                                <Button
                                  color="danger"
                                  onClick={showRejectModal}
                                  type="button"
                                  className="btn-dim btn btn-secondary"
                                >
                                  Reject
                                </Button>
                              )}
                            </li>
                          </ul>
                        </div>
                      ))}

                    {/* Release button for approved work orders */}
                    {workOrder.status === "Approved" && !isAmendmentView && (
                      <div className="actions clearfix">
                        <ul
                          className="m-auto d-flex align-items-center justify-content-center p-0"
                          style={{ flexDirection: "row-reverse" }}
                        >
                          <li className="pt-0 pb-0">
                            <Button
                              color="success"
                              onClick={handleReleaseWorkOrder}
                            >
                              <Icon name="send" /> Release Work Order
                            </Button>
                          </li>
                        </ul>
                      </div>
                    )}

                    <div className="actions clearfix">
                      <ul
                        className="m-auto d-flex align-items-center justify-content-center p-0"
                        style={{ flexDirection: "row-reverse" }}
                      >
                        <li className="pt-0 pb-0">
                          <Dropdown
                            isOpen={shareOptionsToggle}
                            toggle={() => {
                              setShareOptionsToggle(!shareOptionsToggle);
                            }}
                          >
                            <DropdownToggle className="btn btn-white btn-outline-light">
                              <Icon name="share"></Icon>
                              <span>Share</span>
                            </DropdownToggle>
                            <DropdownMenu>
                              <DropdownItem onClick={() => {}}>
                                <Icon name="mail"></Icon>
                                <span>Send Mail</span>
                              </DropdownItem>
                              <DropdownItem onClick={() => {}}>
                                <Icon name="download"></Icon>
                                <span>Export PDF</span>
                              </DropdownItem>
                            </DropdownMenu>
                          </Dropdown>
                        </li>

                        <li className="pt-0 pb-0">
                          <Button
                            className="toggle"
                            color="primary"
                            onClick={() => {
                              if (
                                isAmendmentView ||
                                workOrder.status === "Changes Requested"
                              ) {
                                navigate("/work-order-contractor-approvals");
                              } else {
                                navigate(-1);
                              }
                            }}
                          >
                            <Icon name="arrow-left" />
                            <span>Back</span>
                          </Button>
                        </li>
                      </ul>
                    </div>
                  </div>
                  <BlockHeadContent className="align-self-start d-lg-none">
                    <Button
                      className={`toggle btn btn-icon btn-trigger mt-n1 ${
                        sm ? "active" : ""
                      }`}
                      onClick={() => setSm(!sm)}
                    >
                      <Icon name="menu-alt-r" />
                    </Button>
                  </BlockHeadContent>
                </BlockBetween>
              </BlockHead>

              <Alert color={"danger"} isOpen={workOrder.status === "Rejected"}>
                <strong>Rejected: </strong>
                {(workOrder as any).rejectionComments || "No reason provided"}
              </Alert>

              <Alert
                color={"warning"}
                isOpen={workOrder.status === "Changes Requested"}
              >
                <strong>Changes Requested: </strong>
                This work order requires amendments based on review feedback.
                Please make the necessary changes and resubmit for approval.
              </Alert>

              {/* Amendment specific alert */}
              {/* {isAmendmentView && amendmentData && (
                <Alert color="warning">
                  <strong>Change Request Pending Review</strong>
                  <br />
                  This work order has a pending change request submitted by{" "}
                  {amendmentData.changeRequestedBy} on{" "}
                  {dayjs(amendmentData.changeRequestDate).format("DD/MM/YYYY")}.
                  Please review the proposed changes and take appropriate
                  action.
                </Alert>
              )} */}

              <div className="content clearfix">
                <AntCollapse
                  defaultActiveKey={["1", "2", "3", "4", "5", "6", "7"]}
                >
                  {/* Section A: General Information */}
                  <Panel
                    header={
                      <div className="d-flex align-items-center justify-content-between">
                        <>📋 General Information</>
                      </div>
                    }
                    key="1"
                  >
                    <Descriptions bordered size="small" column={2}>
                      <Descriptions.Item label="Work Order Number">
                        {workOrder.workOrderNumber}
                      </Descriptions.Item>
                      <Descriptions.Item label="Contract Date">
                        {workOrder.contractDate
                          ? dayjs(workOrder.contractDate).format("DD-MMM-YYYY")
                          : "-"}
                      </Descriptions.Item>
                      <Descriptions.Item label="Work Order Title" span={2}>
                        {workOrder.workOrderTitle}
                      </Descriptions.Item>
                      <Descriptions.Item label="Site">
                        {workOrder.siteName}
                      </Descriptions.Item>
                      <Descriptions.Item label="Project">
                        {workOrder.projectName || "Not selected"}
                      </Descriptions.Item>
                      <Descriptions.Item label="Contract Type">
                        {workOrder.contractType}
                      </Descriptions.Item>
                      <Descriptions.Item label="Contract With">
                        {workOrder.contractWith}
                      </Descriptions.Item>
                      <Descriptions.Item label="Contractor">
                        {workOrder.contractorName}
                      </Descriptions.Item>
                      <Descriptions.Item label="Work Order Owner">
                        {workOrder.workOrderOwnerName}
                      </Descriptions.Item>
                      <Descriptions.Item label="Planned Start">
                        {workOrder.plannedStartDate
                          ? dayjs(workOrder.plannedStartDate).format(
                              "DD-MMM-YYYY"
                            )
                          : "-"}
                      </Descriptions.Item>
                      <Descriptions.Item label="Planned End">
                        {workOrder.plannedEndDate
                          ? dayjs(workOrder.plannedEndDate).format(
                              "DD-MMM-YYYY"
                            )
                          : "-"}
                      </Descriptions.Item>
                      <Descriptions.Item label="Approval Required">
                        <Tag
                          color={
                            workOrder.approvalRequired ? "orange" : "green"
                          }
                        >
                          {workOrder.approvalRequired ? "Yes" : "No"}
                        </Tag>
                      </Descriptions.Item>
                      <Descriptions.Item label="Tax Enabled" span={2}>
                        <Tag color={workOrder.enableTax ? "blue" : "default"}>
                          {workOrder.enableTax
                            ? `Yes (${
                                workOrder.taxApplicationLevel || "Not specified"
                              })`
                            : "No"}
                        </Tag>
                        {workOrder.enableTax &&
                          (workOrder.taxApplicationLevel === "Header" ||
                            workOrder.taxApplicationLevel === "Both") &&
                          workOrder.headerTaxName && (
                            <div className="mt-2">
                              <small className="text-muted">
                                Header Tax Type:{" "}
                              </small>
                              <Tag color="cyan">{workOrder.headerTaxName}</Tag>
                            </div>
                          )}
                        {workOrder.enableTax &&
                          (workOrder.taxApplicationLevel === "Both" ||
                            workOrder.taxApplicationLevel === "Header") && (
                            <div className="mt-2">
                              {workOrder.tdsTaxName && (
                                <div>
                                  <small className="text-muted">TDS: </small>
                                  <Tag color="orange">
                                    {workOrder.tdsTaxName}
                                  </Tag>
                                  <small className="text-info">
                                    {" "}
                                    (Deducted from gross total)
                                  </small>
                                </div>
                              )}
                              {workOrder.tcsTaxName && (
                                <div>
                                  <small className="text-muted">TCS: </small>
                                  <Tag color="purple">
                                    {workOrder.tcsTaxName}
                                  </Tag>
                                  <small className="text-success">
                                    {" "}
                                    (Added to gross total)
                                  </small>
                                </div>
                              )}
                            </div>
                          )}
                        {/* {workOrder.budgetName && (
                          <div className="mt-2">
                            <small className="text-muted">Budget: </small>
                            <Tag color="green">{workOrder.budgetName}</Tag>
                          </div>
                        )} */}
                      </Descriptions.Item>
                    </Descriptions>

                    {workOrder.workOrderDescription && (
                      <div className="mt-3">
                        <strong>Description:</strong>
                        <p className="mt-1 text-muted">
                          {workOrder.workOrderDescription}
                        </p>
                      </div>
                    )}
                  </Panel>

                  {/* Section B: Work Order Lines */}
                  <Panel
                    header={`📋 Work Order Lines (${
                      workOrder.workOrderLines?.length || 0
                    })`}
                    key="2"
                  >
                    <div className="mb-3">
                      <Tag color="blue">
                        {workOrder.workOrderLines?.length || 0} line(s)
                      </Tag>
                      {workOrder.budgetName && (
                        <Tag color="green">Budget : {workOrder.budgetName}</Tag>
                      )}
                    </div>

                    <div className="table-responsive poTable">
                      <div
                        style={{
                          width: screenSize - 170 + "px",
                          margin: "auto",
                        }}
                      >
                        {!workOrder.workOrderLines ||
                        workOrder.workOrderLines.length === 0 ? (
                          <div className="text-center py-5">
                            <Empty description="No work order lines found" />
                          </div>
                        ) : (
                          <Table
                            className="customTable"
                            columns={workOrderLinesColumns}
                            dataSource={workOrder.workOrderLines}
                            scroll={{ x: 1400 }}
                            bordered
                            size="small"
                            pagination={false}
                            rowKey="id"
                          />
                        )}
                      </div>

                      {workOrder.workOrderLines?.length > 0 && (
                        <div className="mt-3 p-3 bg-light rounded">
                          <Row>
                            <Col lg="8">
                              <div className="d-flex justify-content-between">
                                <span>Subtotal:</span>
                                <strong>₹{subtotal.toLocaleString()}</strong>
                              </div>
                              {workOrder.enableTax &&
                                lineLevelTaxAmount > 0 && (
                                  <div className="d-flex justify-content-between">
                                    <span>Line Level Tax:</span>
                                    <strong>
                                      ₹{lineLevelTaxAmount.toLocaleString()}
                                    </strong>
                                  </div>
                                )}
                              {workOrder.enableTax && tcsAmount > 0 && (
                                <div className="d-flex justify-content-between">
                                  <span>TCS Amount:</span>
                                  <strong className="text-success">
                                    ₹{tcsAmount.toLocaleString()}
                                  </strong>
                                </div>
                              )}
                              <hr className="my-2" />
                              <div className="d-flex justify-content-between">
                                <span>
                                  <strong>Gross Total:</strong>
                                </span>
                                <strong className="text-success">
                                  ₹{grossTotal.toLocaleString()}
                                </strong>
                              </div>
                              {workOrder.enableTax && tdsAmount > 0 && (
                                <div className="d-flex justify-content-between">
                                  <span>TDS Deduction:</span>
                                  <strong className="text-danger">
                                    -₹{tdsAmount.toLocaleString()}
                                  </strong>
                                </div>
                              )}
                              {paymentDeductions > 0 && (
                                <div className="d-flex justify-content-between">
                                  <span>Payment Deductions:</span>
                                  <strong className="text-danger">
                                    -₹{paymentDeductions.toLocaleString()}
                                  </strong>
                                </div>
                              )}
                              {(tdsAmount > 0 || paymentDeductions > 0) && (
                                <>
                                  <hr className="my-2" />
                                  <div className="d-flex justify-content-between">
                                    <span>
                                      <strong>Net Payable:</strong>
                                    </span>
                                    <strong className="text-primary">
                                      ₹{netPayable.toLocaleString()}
                                    </strong>
                                  </div>
                                </>
                              )}
                            </Col>
                          </Row>
                        </div>
                      )}
                    </div>
                  </Panel>

                  {/* Section C: General Conditions */}
                  <Panel
                    header={`📋 General Conditions (${
                      workOrder.generalConditions?.length || 0
                    })`}
                    key="3"
                  >
                    <div className="mb-3">
                      <Tag color="blue">
                        {workOrder.generalConditions?.length || 0} condition(s)
                        selected
                      </Tag>
                    </div>

                    {workOrder.generalConditions &&
                    workOrder.generalConditions.length > 0 ? (
                      <div>
                        {workOrder.generalConditions
                          .sort((a, b) => a.order - b.order)
                          .map((condition: any, index) => {
                            const hasContent =
                              condition.content &&
                              condition.content.trim() !== "";
                            const hasAttachments =
                              condition.attachments &&
                              condition.attachments.length > 0;

                            return (
                              <div
                                key={condition.id}
                                className="mb-4 p-3 border rounded"
                              >
                                <div className="d-flex justify-content-between align-items-start mb-2">
                                  <h6 className="mb-0">
                                    <FileTextOutlined className="me-2" />
                                    {index + 1}. {condition.conditionCode}
                                  </h6>
                                  <div>
                                    {hasContent && (
                                      <Tag color="green">Text</Tag>
                                    )}
                                    {hasAttachments && (
                                      <Tag color="blue">Documents</Tag>
                                    )}
                                  </div>
                                </div>

                                {/* Text Content */}
                                {hasContent && (
                                  <div className="mb-3">
                                    <div className="p-2 bg-light rounded">
                                      <small className="text-primary fw-medium">
                                        Content:
                                      </small>
                                      <p
                                        className="mb-0 mt-1 small"
                                        style={{ whiteSpace: "pre-wrap" }}
                                      >
                                        {condition.content}
                                      </p>
                                    </div>
                                  </div>
                                )}

                                {/* Attachments */}
                                {hasAttachments && (
                                  <div className="mt-2">
                                    <small className="text-primary fw-medium d-block mb-2">
                                      <PaperClipOutlined className="me-1" />
                                      Documents ({condition.attachments.length}
                                      ):
                                    </small>
                                    <div className="row">
                                      {condition.attachments.map(
                                        (attachment: any, idx: any) => (
                                          <div
                                            key={attachment.id}
                                            className="col-md-6 mb-2"
                                          >
                                            <div className="border rounded p-2 d-flex justify-content-between align-items-center">
                                              <div className="d-flex align-items-center">
                                                <FileTextOutlined className="text-muted me-2" />
                                                <div>
                                                  <div
                                                    className="small fw-medium"
                                                    title={attachment.fileName}
                                                  >
                                                    {attachment.fileName
                                                      .length > 25
                                                      ? `${attachment.fileName.substring(
                                                          0,
                                                          25
                                                        )}...`
                                                      : attachment.fileName}
                                                  </div>
                                                  <small className="text-muted">
                                                    {(
                                                      attachment.fileSize /
                                                      1024 /
                                                      1024
                                                    ).toFixed(2)}
                                                    MB
                                                  </small>
                                                </div>
                                              </div>
                                              <div>
                                                <small className="text-muted">
                                                  {dayjs(
                                                    attachment.uploadDate
                                                  ).format("DD/MM/YY")}
                                                </small>
                                              </div>
                                            </div>
                                          </div>
                                        )
                                      )}
                                    </div>
                                  </div>
                                )}
                              </div>
                            );
                          })}
                      </div>
                    ) : (
                      <div className="text-center text-muted py-3">
                        No general conditions selected
                      </div>
                    )}
                  </Panel>

                  {/* Section D: Payment Conditions */}
                  <Panel header="📋 Payment Conditions" key="4">
                    <div className="mb-3">
                      <Tag color="green">
                        <DollarOutlined className="me-1" />
                        Contract Value: ₹{grossTotal.toLocaleString()}
                      </Tag>
                    </div>

                    {workOrder?.paymentConditions &&
                    isPaymentConditionsArray(workOrder.paymentConditions) &&
                    workOrder.paymentConditions.length > 0 ? (
                      <div className="payment-conditions-container">
                        {(
                          workOrder.paymentConditions as PaymentCondition[]
                        ).map((condition: PaymentCondition, index: number) => (
                          <div
                            key={condition.id || index}
                            className="mb-4 p-3 border rounded"
                          >
                            <div className="d-flex justify-content-between align-items-start mb-2">
                              <h6 className="mb-0 text-primary">
                                <DollarOutlined className="me-2" />
                                {condition.conditionType}
                              </h6>
                              <div>
                                <Tag
                                  color={condition.isPercent ? "blue" : "green"}
                                >
                                  {condition.isPercent
                                    ? "Percentage"
                                    : "Fixed Amount"}
                                </Tag>
                                {condition.interestCharged && (
                                  <Tag color="orange">Interest Charged</Tag>
                                )}
                              </div>
                            </div>

                            <Descriptions bordered size="small" column={2}>
                              <Descriptions.Item label="Amount" span={2}>
                                {condition.isPercent ? (
                                  <div>
                                    <strong>{condition.percentValue}%</strong>{" "}
                                    of contract value
                                    <br />
                                    <small className="text-success">
                                      Calculated: ₹
                                      {condition.calculatedAmount?.toLocaleString() ||
                                        "0"}
                                    </small>
                                  </div>
                                ) : (
                                  <div>
                                    <strong>
                                      ₹
                                      {condition.amountValue?.toLocaleString() ||
                                        "0"}
                                    </strong>
                                    <br />
                                    <small className="text-success">
                                      Fixed Amount: ₹
                                      {condition.calculatedAmount?.toLocaleString() ||
                                        condition.amountValue?.toLocaleString() ||
                                        "0"}
                                    </small>
                                  </div>
                                )}
                              </Descriptions.Item>

                              {condition.interestCharged &&
                                condition.interestRate > 0 && (
                                  <Descriptions.Item label="Interest Rate">
                                    <Tag color="orange">
                                      {condition.interestRate}% per annum
                                    </Tag>
                                  </Descriptions.Item>
                                )}

                              <Descriptions.Item label="Recovery Type">
                                <Tag color="purple">
                                  {condition.recoveryType}
                                </Tag>
                              </Descriptions.Item>

                              {condition.releaseDate && (
                                <Descriptions.Item
                                  label="Release Date"
                                  span={2}
                                >
                                  <div>
                                    <strong>
                                      {dayjs(condition.releaseDate).format(
                                        "DD MMM YYYY"
                                      )}
                                    </strong>
                                    <br />
                                    <small className="text-muted">
                                      {dayjs(condition.releaseDate).fromNow()}
                                    </small>
                                  </div>
                                </Descriptions.Item>
                              )}

                              {/* {condition.notes && (
                                <Descriptions.Item label="Notes" span={2}>
                                  <div className="p-2 bg-light rounded">
                                    <small className="text-muted">{condition.notes}</small>
                                  </div>
                                </Descriptions.Item>
                              )} */}
                            </Descriptions>

                            <div className="mt-2 text-end">
                              <small className="text-muted">
                                Created:{" "}
                                {dayjs(condition.createdAt).format(
                                  "DD-MMM-YYYY HH:mm"
                                )}
                                {condition.updatedAt !==
                                  condition.createdAt && (
                                  <>
                                    {" "}
                                    | Updated:{" "}
                                    {dayjs(condition.updatedAt).format(
                                      "DD-MMM-YYYY HH:mm"
                                    )}
                                  </>
                                )}
                              </small>
                            </div>
                          </div>
                        ))}

                        {/* Summary Card */}
                        <div className="mt-4 p-3 bg-light rounded">
                          <h6 className="mb-3">Payment Conditions Summary</h6>
                          <div className="row">
                            {(
                              workOrder.paymentConditions as PaymentCondition[]
                            ).map((condition: PaymentCondition) => (
                              <div key={condition.id} className="col-md-4 mb-2">
                                <div className="text-center">
                                  <div className="fw-bold text-primary">
                                    ₹
                                    {condition.calculatedAmount?.toLocaleString() ||
                                      "0"}
                                  </div>
                                  <small className="text-muted">
                                    {condition.conditionType}
                                  </small>
                                </div>
                              </div>
                            ))}
                          </div>
                          <hr />
                          <div className="row">
                            <div className="col-md-6">
                              <strong>Total Advance/Deductions: </strong>
                              <span className="text-success">
                                ₹
                                {(
                                  workOrder.paymentConditions as PaymentCondition[]
                                )
                                  .reduce(
                                    (
                                      sum: number,
                                      condition: PaymentCondition
                                    ) =>
                                      sum + (condition.calculatedAmount || 0),
                                    0
                                  )
                                  .toLocaleString()}
                              </span>
                            </div>
                            <div className="col-md-6">
                              <strong>Net Payable: </strong>
                              <span className="text-primary">
                                ₹{netPayable.toLocaleString()}
                              </span>
                              <br />
                              <small className="text-muted">
                                (Gross Total - TDS - Payment Deductions)
                              </small>
                            </div>
                          </div>
                        </div>
                      </div>
                    ) : workOrder?.paymentConditions &&
                      !isPaymentConditionsArray(workOrder.paymentConditions) ? (
                      // Legacy object format - show message that it's not supported in this view
                      <div className="text-center text-muted py-3">
                        <div className="mb-2">
                          <Tag color="warning">Legacy Format</Tag>
                        </div>
                        <p>
                          Payment conditions are in legacy format. Please
                          contact administrator.
                        </p>
                      </div>
                    ) : (
                      <div className="text-center text-muted py-3">
                        <Empty description="No payment conditions configured" />
                      </div>
                    )}
                  </Panel>

                  {/* Amendment specific sections */}
                  {isAmendmentView && amendmentData && (
                    <>
                      {/* Change Request Details Panel */}
                      <Panel
                        header={
                          <span>
                            Change Request Details
                            <Badge
                              color="primary"
                              style={{ marginLeft: "10px" }}
                            >
                              {amendmentData?.changeRequestComments?.length ||
                                0}{" "}
                              Comments
                            </Badge>
                            <Badge color="info" style={{ marginLeft: "10px" }}>
                              {amendmentData?.changeRequestFiles?.length || 0}{" "}
                              Files
                            </Badge>
                          </span>
                        }
                        key="7"
                      >
                        <Row>
                          <Col md={6}>
                            <h6>Comments</h6>
                            <Timeline>
                              {amendmentData?.changeRequestComments?.map(
                                (comment: any, index: any) => (
                                  <Timeline.Item
                                    key={index}
                                    color={
                                      comment.commentRole === "Contractor"
                                        ? "blue"
                                        : "green"
                                    }
                                  >
                                    <div>
                                      <div className="d-flex justify-content-between align-items-center">
                                        <strong>{comment.commentBy}</strong>
                                        <div>
                                          <Badge
                                            color={
                                              comment.commentRole ===
                                              "Contractor"
                                                ? "primary"
                                                : "success"
                                            }
                                            style={{ marginRight: "10px" }}
                                          >
                                            {comment.commentRole}
                                          </Badge>
                                          <Badge color="secondary">
                                            {comment.changeType}
                                          </Badge>
                                        </div>
                                      </div>
                                      <small className="text-muted">
                                        {dayjs(comment.commentDate).format(
                                          "DD-MMM-YYYY HH:mm"
                                        )}
                                      </small>
                                      <p className="mt-2">
                                        {comment.commentText}
                                      </p>
                                    </div>
                                  </Timeline.Item>
                                )
                              )}

                              {amendmentData?.buyerReviewComments &&
                                amendmentData.buyerReviewComments.map(
                                  (comment: any, index: any) => (
                                    <Timeline.Item
                                      key={`buyer-${index}`}
                                      color="purple"
                                    >
                                      <div>
                                        <div className="d-flex justify-content-between align-items-center">
                                          <strong>{comment.commentBy}</strong>
                                          <div>
                                            <Badge color="warning">
                                              {comment.commentRole}
                                            </Badge>
                                            {comment.isInternal && (
                                              <Badge
                                                color="secondary"
                                                className="ml-2"
                                              >
                                                Internal
                                              </Badge>
                                            )}
                                          </div>
                                        </div>
                                        <small className="text-muted">
                                          {dayjs(comment.commentDate).format(
                                            "DD-MMM-YYYY HH:mm"
                                          )}
                                        </small>
                                        <p className="mt-2">
                                          {comment.commentText}
                                        </p>
                                      </div>
                                    </Timeline.Item>
                                  )
                                )}
                            </Timeline>
                          </Col>

                          <Col md={6}>
                            <h6>Uploaded Files</h6>
                            {amendmentData?.changeRequestFiles &&
                            amendmentData.changeRequestFiles.length > 0 ? (
                              <div>
                                {amendmentData.changeRequestFiles.map(
                                  (file: any, index: any) => (
                                    <div
                                      key={index}
                                      className="card card-bordered mb-3"
                                    >
                                      <div className="card-inner">
                                        <div className="d-flex align-items-center">
                                          <div
                                            className="mr-3"
                                            style={{ fontSize: "24px" }}
                                          >
                                            {getFileIcon(file.fileType)}
                                          </div>
                                          <div className="flex-grow-1">
                                            <h6 className="mb-1">
                                              {file.fileName}
                                            </h6>
                                            <div className="text-muted">
                                              <small>
                                                {formatFileSize(
                                                  file.fileSize || 0
                                                )}{" "}
                                                • {file.category || "General"}
                                              </small>
                                            </div>
                                            <div className="text-muted">
                                              <small>
                                                By:{" "}
                                                {file.uploadedBy || "Unknown"} •{" "}
                                                {dayjs(
                                                  file.uploadedDate ||
                                                    file.createdAt
                                                ).format("DD-MMM-YYYY")}
                                              </small>
                                            </div>
                                            {file.description && (
                                              <div className="text-muted">
                                                <small>
                                                  {file.description}
                                                </small>
                                              </div>
                                            )}
                                          </div>
                                          <div className="d-flex">
                                            <Button
                                              size="sm"
                                              color="primary"
                                              outline
                                              className="mr-1"
                                              onClick={() => viewFile(file)}
                                            >
                                              <Icon name="eye" />
                                            </Button>
                                            <Button
                                              size="sm"
                                              color="success"
                                              outline
                                              onClick={() => downloadFile(file)}
                                            >
                                              <Icon name="download" />
                                            </Button>
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  )
                                )}
                              </div>
                            ) : (
                              <div className="text-center text-muted py-3">
                                No files uploaded
                              </div>
                            )}
                          </Col>
                        </Row>
                      </Panel>
                    </>
                  )}
                </AntCollapse>
              </div>
            </div>
          </div>
        </Card>
      </Content>
    </>
  );
};

export default WorkOrderView;
